import React, { useEffect, useState } from "react";
import "./style.css";
import vclogo from "../../assets/logo.png";
import { getBrands } from "../../globalService/api.service";
import { useNavigate } from "react-router-dom";

export default function Landing() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getAllBrands();
  }, []);

  const getAllBrands = async () => {
    try {
      setLoading(true);
      const data = await getBrands();
      setBrands(data || []);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBrands = () => {
    const section = document.getElementById("brands");
    if (section) section.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToFeatures = () => {
    const section = document.getElementById("features");
    if (section) section.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="vc-landing">
      <header className="vc-navbar">
        <div className="vc-shell vc-navbar-inner">
          <div className="vc-navbar-brand">
            <img src={vclogo} alt="Virtual Challenge Logo" className="vc-navbar-logo" />
            <div className="vc-navbar-title-wrap">
              <span className="vc-navbar-title">Virtual Challenge</span>
            </div>
          </div>

          <div className="vc-navbar-actions">
            <button className="vc-btn-secondary" onClick={scrollToFeatures}>
              Learn More
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="vc-hero">
          <div className="vc-shell vc-hero-grid">
            <div className="vc-hero-copy">
              <div className="vc-hero-badge">Challenge. Compete. Conquer.</div>

              <h1 className="vc-hero-title">
                Welcome to <span>Virtual Challenge</span>
              </h1>

              <p className="vc-hero-text">
                Discover exciting brands, immersive experiences, and a new way to
                connect with digital challenges. Explore our platform and start your
                next adventure today.
              </p>

              <div className="vc-hero-actions">
                <button className="vc-btn-primary" onClick={scrollToBrands}>
                  Start Exploring
                </button>
                <button className="vc-btn-secondary" onClick={scrollToBrands}>
                  View Brands
                </button>
              </div>

              <div className="vc-hero-metrics">
                <div className="vc-hero-metric">
                  <div className="vc-hero-metric-value">{brands.length}+</div>
                  <div className="vc-hero-metric-label">Brands</div>
                </div>
                <div className="vc-hero-metric">
                  <div className="vc-hero-metric-value">24/7</div>
                  <div className="vc-hero-metric-label">Access</div>
                </div>
                <div className="vc-hero-metric">
                  <div className="vc-hero-metric-value">100%</div>
                  <div className="vc-hero-metric-label">Engagement</div>
                </div>
              </div>
            </div>

            <div className="vc-hero-side">
              <div className="vc-hero-card">
                <div className="vc-hero-card-label">Why Choose Us</div>
                <h2 className="vc-hero-card-title">Built for modern digital experiences</h2>
                <p className="vc-hero-card-text">
                  We bring together unique brands and engaging experiences in one
                  modern platform built for discovery, interaction, and growth.
                </p>

                <div className="vc-hero-stack">
                  <div className="vc-mini-card">
                    <div>
                      <h4>Premium Brand Showcase</h4>
                      <p>Highlight each experience with a clean, focused layout.</p>
                    </div>
                    <span className="vc-mini-pill">Explore</span>
                  </div>

                  <div className="vc-mini-card">
                    <div>
                      <h4>Fast Navigation</h4>
                      <p>Move directly into each brand experience in one click.</p>
                    </div>
                    <span className="vc-mini-pill">Direct</span>
                  </div>

                  <div className="vc-mini-card">
                    <div>
                      <h4>Immersive Platform</h4>
                      <p>A polished interface designed to feel elevated and modern.</p>
                    </div>
                    <span className="vc-mini-pill">Premium</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="vc-section vc-stats-strip">
          <div className="vc-shell">
            <div className="vc-stats-grid">
              <div className="vc-stat-card">
                <div className="vc-stat-value">{brands.length}+</div>
                <div className="vc-stat-label">Partner Brands</div>
              </div>
              <div className="vc-stat-card">
                <div className="vc-stat-value">5000+</div>
                <div className="vc-stat-label">Active Users</div>
              </div>
              <div className="vc-stat-card">
                <div className="vc-stat-value">120+</div>
                <div className="vc-stat-label">Challenges Hosted</div>
              </div>
              <div className="vc-stat-card">
                <div className="vc-stat-value">98%</div>
                <div className="vc-stat-label">Satisfaction</div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="vc-section">
          <div className="vc-shell">
            <div className="vc-section-header">
              <div className="vc-section-eyebrow">What We Offer</div>
              <h2 className="vc-section-title">A platform built for engagement</h2>
              <p className="vc-section-text">
                Virtual Challenge combines strong presentation, smooth interaction,
                and brand-focused navigation into one powerful landing experience.
              </p>
            </div>

            <div className="vc-feature-grid">
              <div className="vc-feature-card">
                <div className="vc-feature-icon">01</div>
                <h3>Immersive Experiences</h3>
                <p>
                  Create a stronger first impression with sections that feel premium,
                  modern, and visually intentional.
                </p>
              </div>

              <div className="vc-feature-card">
                <div className="vc-feature-icon">02</div>
                <h3>Brand Discovery</h3>
                <p>
                  Help users browse available brands quickly through a clean and
                  beautifully organized interface.
                </p>
              </div>

              <div className="vc-feature-card">
                <div className="vc-feature-icon">03</div>
                <h3>Simple Navigation</h3>
                <p>
                  Route visitors directly into the experience they want with clear,
                  prominent calls to action.
                </p>
              </div>

              <div className="vc-feature-card">
                <div className="vc-feature-icon">04</div>
                <h3>Modern Visual Design</h3>
                <p>
                  Use spacing, contrast, and motion-ready styling to make the site
                  feel elevated from the first second.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="vc-section">
          <div className="vc-shell">
            <div className="vc-split">
              <div className="vc-panel">
                <h3>Designed for discovery</h3>
                <p>
                  The landing page should do more than list brands. It should guide
                  users into a premium experience that feels curated and intentional.
                </p>

                <div className="vc-bullet-list">
                  <div className="vc-bullet-item">
                    <span className="vc-bullet-dot" />
                    <span>Strong hero messaging that explains the platform instantly.</span>
                  </div>
                  <div className="vc-bullet-item">
                    <span className="vc-bullet-dot" />
                    <span>Visual hierarchy that makes important actions obvious.</span>
                  </div>
                  <div className="vc-bullet-item">
                    <span className="vc-bullet-dot" />
                    <span>Branded sections that feel polished across desktop and mobile.</span>
                  </div>
                </div>
              </div>

              <div className="vc-panel">
                <h3>Built to scale with your brands</h3>
                <p>
                  As your brand list grows, this layout keeps everything organized and
                  easy to navigate without feeling crowded or unfinished.
                </p>

                <div className="vc-bullet-list">
                  <div className="vc-bullet-item">
                    <span className="vc-bullet-dot" />
                    <span>Responsive card grid for brand entries.</span>
                  </div>
                  <div className="vc-bullet-item">
                    <span className="vc-bullet-dot" />
                    <span>Reusable section styles for future testimonials or FAQs.</span>
                  </div>
                  <div className="vc-bullet-item">
                    <span className="vc-bullet-dot" />
                    <span>Consistent spacing and layout structure throughout the page.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="brands" className="vc-section vc-brands-section">
          <div className="vc-shell">
            <div className="vc-section-header">
              <div className="vc-section-eyebrow">Explore</div>
              <h2 className="vc-section-title">Our Brands</h2>
              <p className="vc-section-text">
                Choose a brand below to enter its dedicated experience.
              </p>
            </div>

            <div className="vc-landing-card">
              <div className="vc-landing-card-title">Available Brands</div>

              {loading ? (
                <div className="vc-landing-loading">
                  <div className="vc-loading-ring" />
                  <span>Loading brands...</span>
                </div>
              ) : brands.length > 0 ? (
                <div className="vc-brands-grid">
                  {brands.map((brand) => (
                    <button
                      key={brand.key}
                      className="vc-brand-btn"
                      onClick={() => navigate(`/${brand.key}`)}
                    >
                      <span>{brand.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="vc-landing-empty">No brands available right now.</div>
              )}
            </div>
          </div>
        </section>

        <section className="vc-section vc-cta">
          <div className="vc-shell">
            <div className="vc-cta-card">
              <h2>Ready to enter the challenge?</h2>
              <p>
                Explore our brands, discover new experiences, and step into a
                platform designed to engage from the very first click.
              </p>
              <button className="vc-btn-primary" onClick={scrollToBrands}>
                Enter Now
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}